<template>
  <div class="space-y-6">

    <!-- Score Summary -->
    <MTCard class="p-6 text-center">
      <div class="flex justify-center mb-4">
        <div
          class="w-24 h-24 rounded-full flex items-center justify-center text-white text-2xl font-bold"
          :class="scoreRingColor"
        >
          {{ score.percent }}%
        </div>
      </div>
      <h2 class="text-xl font-bold mb-1">{{ encouragement }}</h2>
      <p class="text-[var(--mt-text-muted)] mb-2">
        {{ score.correct }} / {{ score.total }} correct
      </p>
      <p v-if="skippedCount > 0" class="text-sm text-yellow-600">
        {{ skippedCount }} question{{ skippedCount > 1 ? 's' : '' }} skipped
      </p>
    </MTCard>

    <!-- Strengths & Weaknesses -->
    <MTCard v-if="summary.strongestTopics.value.length || summary.weakestTopics.value.length" class="p-6">
      <h3 class="font-semibold mb-4">Strengths & Weaknesses</h3>

      <div v-if="summary.strongestTopics.value.length" class="mb-4">
        <p class="text-sm font-medium text-green-500 mb-2">Strongest topics</p>
        <div class="space-y-2">
          <div v-for="t in summary.strongestTopics.value" :key="t.sgId" class="flex items-center justify-between">
            <div class="flex items-center gap-2 min-w-0">
              <MTIcon name="check-circle" :size="16" class="text-green-500 shrink-0" />
              <router-link
                v-if="t.learnUrl"
                :to="t.learnUrl"
                class="text-sm truncate revision-link"
              >{{ t.title }}</router-link>
              <span v-else class="text-sm truncate">{{ t.title }}</span>
            </div>
            <div class="flex items-center gap-2 shrink-0">
              <span class="text-sm font-medium">{{ t.sessionCorrect }}/{{ t.sessionTotal }}</span>
              <MTBadge :color="bandColor(t.band)">{{ t.band }}</MTBadge>
            </div>
          </div>
        </div>
      </div>

      <div v-if="summary.weakestTopics.value.length">
        <p class="text-sm font-medium text-red-400 mb-2">Topics to work on</p>
        <div class="space-y-2">
          <div v-for="t in summary.weakestTopics.value" :key="t.sgId" class="flex items-center justify-between">
            <div class="flex items-center gap-2 min-w-0">
              <MTIcon name="alert-triangle" :size="16" class="text-orange-500 shrink-0" />
              <router-link
                v-if="t.learnUrl"
                :to="t.learnUrl"
                class="text-sm truncate revision-link"
              >{{ t.title }}</router-link>
              <span v-else class="text-sm truncate">{{ t.title }}</span>
            </div>
            <div class="flex items-center gap-2 shrink-0">
              <span class="text-sm font-medium">{{ t.sessionCorrect }}/{{ t.sessionTotal }}</span>
              <MTBadge :color="bandColor(t.band)">{{ t.band }}</MTBadge>
            </div>
          </div>
        </div>
      </div>
    </MTCard>

    <!-- Progress Over Time -->
    <MTCard v-if="summary.overallStats.value.totalRevisions > 0" class="p-6">
      <h3 class="font-semibold mb-3">Progress Over Time</h3>

      <!-- Trend -->
      <div v-if="summary.progressTrend.value.previousAverage !== null" class="mb-3">
        <span class="text-sm" :class="trendColor">
          <MTIcon :name="trendIcon" :size="14" class="inline" />
          {{ trendText }}
        </span>
      </div>

      <!-- Mini score history -->
      <div v-if="summary.progressTrend.value.history.length > 1" class="flex items-end gap-2 mb-3">
        <div
          v-for="(s, i) in summary.progressTrend.value.history.slice().reverse()"
          :key="i"
          class="flex flex-col items-center gap-1"
        >
          <div
            class="w-8 rounded-t text-xs text-center text-white font-medium flex items-end justify-center"
            :class="s >= 80 ? 'bg-green-500' : s >= 60 ? 'bg-yellow-500' : s >= 40 ? 'bg-orange-500' : 'bg-red-500'"
            :style="{ height: Math.max(20, s * 0.6) + 'px' }"
          >
            {{ s }}
          </div>
        </div>
      </div>

      <!-- Overall stats -->
      <div class="grid grid-cols-3 gap-4 text-center text-sm">
        <div>
          <div class="font-semibold text-lg">{{ summary.overallStats.value.totalRevisions }}</div>
          <div class="text-[var(--mt-text-muted)]">Revisions</div>
        </div>
        <div>
          <div class="font-semibold text-lg">{{ summary.overallStats.value.averageScore }}%</div>
          <div class="text-[var(--mt-text-muted)]">Average</div>
        </div>
        <div>
          <div class="font-semibold text-lg">{{ summary.overallStats.value.bestScore }}%</div>
          <div class="text-[var(--mt-text-muted)]">Best</div>
        </div>
      </div>
    </MTCard>

    <!-- Topic Breakdown -->
    <MTCard class="p-6">
      <h3 class="font-semibold mb-4">Topic Breakdown</h3>
      <div class="space-y-3">
        <div v-for="t in summary.allTopicsEnriched.value" :key="t.sgId">
          <div class="flex items-center justify-between mb-1">
            <div class="flex items-center gap-2 min-w-0">
              <router-link
                v-if="t.learnUrl"
                :to="t.learnUrl"
                class="text-sm truncate revision-link"
              >{{ t.title }}</router-link>
              <span v-else class="text-sm truncate">{{ t.title }}</span>
              <MTBadge :color="bandColor(t.band)">{{ t.band }}</MTBadge>
            </div>
            <span class="text-sm font-medium shrink-0">{{ t.sessionCorrect }}/{{ t.sessionTotal }}</span>
          </div>
          <MTProgress
            :progress="t.sessionPercent"
            size="xs"
            :showLabel="false"
          />
        </div>
      </div>
    </MTCard>

    <!-- Per-Question Review -->
    <MTCard class="p-6">
      <h3 class="font-semibold mb-4">Question Review</h3>
      <div class="space-y-2">
        <div v-for="(item, idx) in questionList" :key="idx">
          <button
            class="w-full flex items-center gap-3 p-3 rounded-lg border hover:bg-[var(--mt-hover)] transition-colors text-left"
            :class="item.isSkipped ? 'question-row-skipped' : item.correct ? 'question-row-correct' : 'question-row-wrong'"
            @click="toggleQuestion(idx)"
          >
            <!-- Status icon -->
            <MTIcon
              v-if="item.isSkipped"
              name="skip-forward"
              :size="18"
              class="text-yellow-500 shrink-0"
            />
            <MTIcon
              v-else
              :name="item.correct ? 'check-circle' : 'x-circle'"
              :size="18"
              :class="item.correct ? 'text-green-500' : 'text-red-500'"
              class="shrink-0"
            />

            <!-- Question number -->
            <span class="text-sm font-medium w-6">Q{{ idx + 1 }}</span>

            <!-- Topic badge -->
            <span class="text-xs px-2 py-0.5 rounded-full bg-[var(--mt-badge-muted-bg)] text-[var(--mt-badge-muted-text)] truncate">
              {{ item.topicTitle }}
            </span>

            <!-- Skipped badge -->
            <MTBadge v-if="item.isSkipped" color="yellow">Skipped</MTBadge>

            <!-- Expand icon -->
            <MTIcon
              :name="expandedQuestions[idx] ? 'chevron-down' : 'chevron-right'"
              :size="16"
              class="ml-auto text-[var(--mt-text-muted)] shrink-0"
            />
          </button>

          <!-- Expanded question details -->
          <div v-if="expandedQuestions[idx]" class="ml-4 mt-2 mb-2">
            <div v-if="item.isSkipped" class="p-3 rounded-lg text-sm mb-2 skipped-message">
              This question was skipped.
            </div>
            <RevisionQuestionReview
              :containerResults="item.containers"
              :sectionGroupId="item.sectionGroupId"
              :studentId="studentId"
              :isSkipped="item.isSkipped"
            />
          </div>
        </div>
      </div>
    </MTCard>

    <!-- Action Buttons -->
    <div class="flex justify-center gap-3 pb-6">
      <MTButton variant="primary" size="lg" @click="$emit('finish')">
        Done
      </MTButton>
      <MTButton variant="secondary" size="lg" @click="$emit('retry')">
        Try Again
      </MTButton>
    </div>
  </div>
</template>

<script setup>
import { computed, reactive } from "vue";
import MTCard from "@/components/ui/MTCard.vue";
import MTButton from "@/components/ui/MTButton.vue";
import MTIcon from "@/components/ui/MTIcon.vue";
import MTBadge from "@/components/ui/MTBadge.vue";
import MTProgress from "@/components/ui/MTProgress.vue";
import RevisionQuestionReview from "./RevisionQuestionReview.vue";
import { useRevisionSummary } from "@/composables/useRevisionSummary";

const props = defineProps({
  results: { type: Object, required: true },
  score: { type: Object, required: true },
  topicBreakdown: { type: Object, required: true },
  childId: { type: String, required: true },
  skippedCount: { type: Number, default: 0 },
  studentId: { type: String, required: true },
  queue: { type: Array, default: () => [] },
  skipped: { type: Object, default: () => new Set() }
});

defineEmits(["finish", "retry"]);

const summary = useRevisionSummary(
  props.childId,
  computed(() => props.topicBreakdown),
  props.studentId
);

const expandedQuestions = reactive({});

function toggleQuestion(idx) {
  expandedQuestions[idx] = !expandedQuestions[idx];
}

const scoreRingColor = computed(() => {
  const p = props.score.percent;
  if (p >= 80) return "bg-green-500";
  if (p >= 60) return "bg-yellow-500";
  if (p >= 40) return "bg-orange-500";
  return "bg-red-500";
});

const encouragement = computed(() => {
  const p = props.score.percent;
  if (p >= 90) return "Outstanding!";
  if (p >= 80) return "Great work!";
  if (p >= 60) return "Good effort!";
  if (p >= 40) return "Keep practising!";
  return "Don't give up!";
});

// Build question list from queue + results + skipped
const questionList = computed(() => {
  return props.queue.map((item, idx) => {
    const result = props.results[idx];
    const isSkipped = props.skipped.has ? props.skipped.has(idx) : false;
    const topicTitle = summary.sgShortTitle(item.sectionGroupId);

    // For skipped questions, build synthetic containers from raw question data
    // so the results page can still show correct answer + solution
    const containers = result?.containers || [];
    const skippedContainers = isSkipped && !containers.length
      ? (item.question?.containers || []).map(c => ({
          questionContents: c.questionContents || [],
          solutionContents: c.solutionContents || [],
          correctAnswer: (c.answerContents || [])[0]?.rowContent?.toString().trim() || null,
          answerDatatype: c.answerDatatype,
          options: c.options || [],
          studentAnswer: null,
          correct: false
        }))
      : containers;

    return {
      sectionGroupId: item.sectionGroupId,
      topicTitle,
      isSkipped,
      correct: result?.correct ?? false,
      containers: skippedContainers,
      question: item.question
    };
  });
});

// Progress trend helpers
const trendColor = computed(() => {
  const d = summary.progressTrend.value.direction;
  if (d === "up") return "text-green-500";
  if (d === "down") return "text-red-500";
  return "text-[var(--mt-text-muted)]";
});

const trendIcon = computed(() => {
  const d = summary.progressTrend.value.direction;
  if (d === "up") return "trending-up";
  if (d === "down") return "trending-down";
  return "minus";
});

const trendText = computed(() => {
  const t = summary.progressTrend.value;
  if (t.direction === "up") return `Up from ${t.previousAverage}% average`;
  if (t.direction === "down") return `Down from ${t.previousAverage}% average`;
  return `Same as ${t.previousAverage}% average`;
});

function bandColor(band) {
  return {
    beginner: "blue",
    developing: "green",
    secure: "yellow",
    confident: "orange",
    mastery: "purple"
  }[band] || "gray";
}
</script>

<style scoped>
.revision-link {
  color: var(--mt-accent-blue);
}
.revision-link:hover {
  opacity: 0.8;
}
.question-row-skipped {
  border-color: #ca8a04;
  background: rgba(202, 138, 4, 0.08);
}
.question-row-correct {
  border-color: #22c55e;
  background: rgba(34, 197, 94, 0.06);
}
.question-row-wrong {
  border-color: #ef4444;
  background: rgba(239, 68, 68, 0.06);
}
.skipped-message {
  background: rgba(202, 138, 4, 0.1);
  color: #eab308;
}
</style>
